import { DatabaseService, DataQualityService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Database, DatabaseBulkDeleteParams, DatabaseBulkUpdateParams, SchemaProperty, UOMS, UomTypes } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useLocation, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { DataQualityEnabledTypes } from '../../common/Constants';
import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes, Query } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { uomConversion } from '../../common/uomConversion';
import { deepCopy, format, generatePluralStr } from '../../common/util';
import DataQualityRuleValidation from '../data-quality/DataQualityRuleValidation';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { ColumnOrderType, DATABASE_ID_COLUMN, EXCLUDED_DATABASE_COLUMNS, FILTER_TOKEN_DISPLAY_LIMIT, OVERSIZED_RESOURCE_THRESHOLDS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { FilterLocationState, SchemaPropertyMap } from './types';
import { generateColumnDefs, getFilterQuery } from './utils';

type DatabaseProps = FlashContextInterface;

const databaseService = new DatabaseService();
const schemaService = new SchemaService();
const dataQualityService = new DataQualityService();
const resourceName = 'databases';
const dataQualityResourceName = 'database';

const DatabasePage: FunctionComponent<DatabaseProps> = ({ flash }) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const location = useLocation<FilterLocationState>();

    const portfolioId = match?.params?.portfolioId;
    const [ databaseItems, setDatabaseItems ] = useState<Database[]>([]);
    const [ databaseSchemas, setDatabaseSchemas ] = useState<SchemaProperty[]>([]);
    const [ databaseSchemaMap, setDatabaseSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ databaseColumnDefinitions, setDatabaseColumnDefinitions ] = useState<ColumnDefinition<Database>[]>([]);
    const [ selectedDatabases, setSelectedDatabases ] = useState<Database[]>([]);
    const [ filteredDatabases, setFilteredDatabases ] = useState<Database[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const [ reloadRulesImmediately, setReloadRulesImmediately ] = useState(true);
    const [ defaultQuery, setDefaultQuery ] = useState<Query>();
    const { updateInlineEditFn } = useContext(InlineEditContext);
    const [ oversizedAlert, setOversizedAlert ] = useState('');

    useEffect(() => {
        if (location?.state?.targetValue && location?.state?.targetColumn) {
            const query = getFilterQuery(location.state.targetValue, location.state.targetColumn);
            setDefaultQuery(query);
        }
    }, [ location ]);
    
    const isMounted = useIsMounted();

    const loadDatabaseData = async () => {
        setLoading(true);
        setSelectedDatabases([]);
        try {
            if (!portfolioId) {
                flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load database data
            const databases = await databaseService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            if (databases.length > OVERSIZED_RESOURCE_THRESHOLDS.database) {
                setOversizedAlert(format(constants.OVERSIZED_PORTFOLIO, [databases.length, resourceName, OVERSIZED_RESOURCE_THRESHOLDS.database]));
            } else {
                setOversizedAlert('');
            }
            setDatabaseItems(databases);

            // Load database schemas
            const databaseSchemas = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            setDatabaseSchemas(databaseSchemas);
            const databaseHeaders: SchemaPropertyMap = {};
            databaseSchemas.forEach(dbSchema => {
                databaseHeaders[dbSchema.value] = dbSchema;
            });
            setDatabaseSchemaMap(databaseHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(databases, databaseHeaders, ColumnOrderType.DATABASE, EXCLUDED_DATABASE_COLUMNS, true);
            // Add mapped columns
            columnDefinitions.push({
                id: 'applicationMapped',
                header: constants.APPLICATION_MAPPED_COLUMN_HEADER,
                type: ColumnTypes.TEXT,
                data: (item: Database) => item.applicationMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                cell: (item: Database) => item.applicationMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
            });
            columnDefinitions.push({
                id: 'serverMapped',
                header: constants.SERVER_MAPPED_COLUMN_HEADER,
                type: ColumnTypes.TEXT,
                data: (item: Database) => item.serverMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                cell: (item: Database) => item.serverMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
            });
            setDatabaseColumnDefinitions(columnDefinitions);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.DATABASE_ERROR_LOADING);
                setDatabaseItems([]);
                setDatabaseColumnDefinitions([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadDatabaseData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: DatabaseBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            databaseIds: [(rowItem as Database).databaseId],
        };

        try {
            await databaseService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.DATABASE_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DATABASE_EDITING_ERROR_HEADING);
        } finally {
            await loadDatabaseData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple databases. If databases are selected, they will be deleted. If there are no selected databases,
     * then all filtered databases will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(false);
        try {
            const selectedDatabaseIds: string[] = (selectedDatabases.length || !filteredDatabases ? selectedDatabases : filteredDatabases)
                .map(selectedDatabase => selectedDatabase.databaseId);

            const conditions: DatabaseBulkDeleteParams = {
                databaseIds: selectedDatabaseIds,
                portfolioId,
            };
            await databaseService.deleteDatabases(conditions); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DATABASE_DELETE_ERROR_HEADING);
        } finally {
            await loadDatabaseData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(true);
        try {
            await databaseService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DATABASE_DELETE_ERROR_HEADING);
        } finally {
            await loadDatabaseData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: InlineEditValue,
        updatedUOM?: UOMS,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(false);
        const selectedSchema = databaseSchemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.DATABASE_EDITING_ERROR_HEADING);
            return;
        }
        const selectedDatabaseIds = (selectAll ? (filteredDatabases ?? databaseItems) : selectedDatabases).map(selectedDb => selectedDb.databaseId);

        let bulkUpdateValue = updatedValue;
        
        // Allow user to unset percentage properties
        if (selectedSchema?.UOM == UomTypes.PERCENTAGE && 
            (!['number', 'string'].includes(typeof(updatedValue)) || updatedValue === '')) {
            bulkUpdateValue = null;
        } else if (selectedSchema.UOM && selectedSchema.dbUOM && updatedUOM) {
            const dbUOM = selectedSchema.dbUOM;
            bulkUpdateValue = uomConversion(Number(updatedValue), updatedUOM, dbUOM);
        }

        const conditions: DatabaseBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: bulkUpdateValue,
            portfolioId,
            databaseIds: selectedDatabaseIds,
        };

        try {
            await databaseService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedDatabaseIds.length || databaseItems.length, constants.DATABASE_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DATABASE_EDITING_ERROR_HEADING);
        } finally {
            await loadDatabaseData();
        }
    };

    const defaultSortingState: SortingState<Database> = {
        isDescending: false,
        sortingColumn: {
            sortingField: DATABASE_ID_COLUMN,
        },
    };

    const onFilterByRuleChange = async (ruleId: string) => {
        if (!portfolioId) {
            return;
        }
        try {
            const invalidDatabaseIds = await dataQualityService.getInvalidRecords(portfolioId, dataQualityResourceName, ruleId);
            const query = getFilterQuery(invalidDatabaseIds, DATABASE_ID_COLUMN);
            setDefaultQuery(query);
        } catch (err) {
            flash.alert(FlashType.error, constants.RULE_FILTER_ERROR_MSG, '');
        }
    };

    const filterChanged = (databases: readonly Database[] | null) => {
        setFilteredDatabases(deepCopy(databases));
    };

    return (
        <SpaceBetween size="m"> 
            {oversizedAlert ? 
                <Alert type='info' header={constants.OVERSIZED_HEADER}>{oversizedAlert}</Alert>
                : (
                    <DataQualityRuleValidation 
                        assetType={DataQualityEnabledTypes.DATABASE}
                        assetItems={databaseItems}
                        reloadImmediately={reloadRulesImmediately}
                        filterOnRule={onFilterByRuleChange}
                    />
                )
            }
            
            <AWSCATTable
                resourceName={constants.DATABASE_TABLE_HEADER}
                resourceType='database'
                items={databaseItems}
                importWhenEmpty={true}
                columnDefinitions={databaseColumnDefinitions}
                loading={loading}
                resizeable={true}
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedDatabases as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='database'
                        resourceName={constants.DATABASE_RESOURCE_NAME}
                        resourceCount={databaseItems.length}
                        selectionCount={selectedDatabases.length}
                        hasFilter={!!filteredDatabases}
                        filteredCount={filteredDatabases?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={databaseSchemas}
                        schemaPropertyMap={databaseSchemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
                defaultQuery={defaultQuery}
                filterTokenDisplayLimit={FILTER_TOKEN_DISPLAY_LIMIT}
            />
        </SpaceBetween>
    );
};

export default withFlashContext(DatabasePage);
